/* eslint-disable complexity */
import type { Space } from '@mail/cross-sizes-utils';
import { SECOND_MS } from '@mail-core/std';
import { logger } from 'lib/logger';
import { xray } from 'lib/xray';
import { splitEvery } from 'ramda';
import { quotaCleanerFastCleanApiCall } from 'reactApp/api/quotaCleaner/QuotaCleanerAPICall';
import type { FastCleanInfo, UserQuotaFastCleanListParams } from 'reactApp/api/quotaCleaner/QuotaCleanerAPICall.types';
import { fastCleanFakeLoading, fastCleanSafeBatchSize } from 'reactApp/appHelpers/featuresHelpers/features/quotaCleaner';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import {
    fastCleanError,
    fastCleanGroupsError,
    fastCleanGroupsStart,
    fastCleanGroupsUpdate,
    fastCleanStart,
    fastCleanSuccess,
} from 'reactApp/modules/userQuotaCleaner/userQuotaCleaner.actions';
import { removableGroupsIds } from 'reactApp/modules/userQuotaCleaner/userQuotaCleaner.constants';
import { FastCleanSelectors } from 'reactApp/modules/userQuotaCleaner/userQuotaCleaner.selectors';
import type { QuotaCleaneFastGroup, QuotaFastCleanerState } from 'reactApp/modules/userQuotaCleaner/userQuotaCleaner.types';
import type { UserQuotaGroupId } from 'reactApp/types/QuotaCleaner';
import { captureException } from 'reactApp/utils/tracer';
import { call, delay, put, select } from 'redux-saga/effects';

import { saveFastCleanGroupsSuccess } from './saveFastCleanGroupsSuccess.saga';

type FastCleanFailResultType = 'no_clean' | 'remove_only' | 'remove_and_safe' | 'unexpected';
type FastCleanSuccessResultType = 'remove_only' | 'remove_and_safe';
export type FastCleanResultType = FastCleanFailResultType | FastCleanSuccessResultType;

export type FastCleanProcessReturnValueType = {
    status: boolean;
    result: FastCleanResultType;
    data: { totalCleared: number; overquotaBytes: number; expectedTotalCleared: number };
};

// eslint-disable-next-line max-lines-per-function
export function* fastCleanProcess(batchSize = fastCleanSafeBatchSize) {
    const { fastCleanGroups: groups }: QuotaFastCleanerState = yield select(FastCleanSelectors.getState);
    const { original: overquotaBytes }: Space = yield select(UserQuotaSelectors.getOverquotaBytes);

    // подготавливаем мапки и параметры
    const groupIdToGroupData = groups.reduce((acc, group) => {
        acc[group.groupId] = group;
        return acc;
    }, {}) as Record<UserQuotaGroupId, QuotaCleaneFastGroup>;

    const groupsIds = Object.keys(groupIdToGroupData) as UserQuotaGroupId[];

    const groupIdToGroupParams: Record<UserQuotaGroupId, UserQuotaFastCleanListParams> | null = yield select(
        FastCleanSelectors.getGroupParams
    );

    if (groups.length === 0 || !groupIdToGroupParams) {
        yield put(fastCleanError());

        return {
            status: false,
            result: 'unexpected',
        };
    }

    const cleared: QuotaFastCleanerState['cleared'] = {
        total: 0,
        mail: {
            removed: 0,
            movedToSafe: 0,
        },
        cloud: {
            removed: 0,
            movedToSafe: 0,
        },
    };
    let remainingGroups = [...groupsIds];
    let clearEndTime = Date.now();

    const removableGroups = groupsIds.filter((groupId) => removableGroupsIds.includes(groupId));
    const safableGroups = groupsIds.filter((groupId) => !removableGroupsIds.includes(groupId));

    const groupsChunks = [removableGroups, ...splitEvery<UserQuotaGroupId>(batchSize || 3, safableGroups)];

    yield put(fastCleanStart());

    for (let i = 0; i < groupsChunks.length; i++) {
        const isRemovableChunk = i === 0;
        const isFirstSafeChunk = i === 1;
        const groupsChunk = groupsChunks[i];
        const groupsChunkParams: UserQuotaFastCleanListParams[] = groupsChunk.map((groupId) => groupIdToGroupParams[groupId]);

        if (groupsChunk.length === 0) {
            continue;
        }

        try {
            yield put(fastCleanGroupsStart(groupsChunk));

            const {
                data: { result, groups },
            }: { data: FastCleanInfo } = yield call(quotaCleanerFastCleanApiCall, { groups: groupsChunkParams });

            cleared.total += result?.total_cleared?.size || 0;
            cleared.mail.movedToSafe += result?.mail_moved_to_safe?.size || 0;
            cleared.mail.removed += result?.mail_deleted?.size || 0;
            cleared.cloud.movedToSafe += result?.cloud_moved_to_safe?.size || 0;
            cleared.cloud.removed += result?.cloud_deleted?.size || 0;

            remainingGroups = remainingGroups.filter((groupId) => !groupsChunk.includes(groupId));

            if (fastCleanFakeLoading) {
                const maxSecondsLeft: number = yield call(saveFastCleanGroupsSuccess, groups);
                const chunkClearEndTime = Date.now() + maxSecondsLeft * SECOND_MS;
                clearEndTime = Math.max(clearEndTime, chunkClearEndTime);
            } else {
                const groupsClearResult = groups.map((group) => ({ groupId: group.group_id, cleared: group.result.total_cleared }));
                yield put(fastCleanGroupsUpdate(groupsClearResult));
            }

            // проверяем исходы
            if (cleared.total >= overquotaBytes) {
                if (fastCleanFakeLoading) {
                    const msToClearEnd = clearEndTime - Date.now();

                    /* eslint-disable max-depth */
                    if (msToClearEnd > 0) {
                        yield delay(msToClearEnd);
                    }
                }

                // вышли из оверквоты и почистили всё, что обещали (возможно больше) – идеальный исход
                yield put(fastCleanSuccess(cleared));

                return {
                    status: true,
                    result: isRemovableChunk ? 'remove_only' : 'remove_and_safe',
                };
            }
            // не вышли из оверквоты и осталось, чего почистить – чистим дальше, пока всё норм
        } catch (error) {
            // сетевая ошибка или ошибка ручки
            // не вышли из оверквоты и осталось что чистить, но произошла ошибка – плохо
            logger.error(error);
            captureException(error, { issueKey: 'fastClean/error' });
            xray.send('quota-fast-cln-err');
            yield put(fastCleanGroupsError(groupsChunk));
            yield put(fastCleanError());

            return {
                status: false,
                result: isRemovableChunk ? 'no_clean' : isFirstSafeChunk ? 'remove_only' : 'remove_and_safe',
            };
        }
    }

    // не вышли из оверквоты, а чистить больше нечего – плохо
    yield put(fastCleanError());

    let result = '';

    if (safableGroups.length !== 0) {
        result = 'remove_and_safe';
    } else if (removableGroups.length !== 0) {
        result = 'remove_only';
    } else {
        result = 'no_clean';
    }

    return {
        status: false,
        result,
    };
}
